/**
 * Reproductor de video de TODA la app (regla: no se usa un <video> pelado; ver
 * docs/arquitectura-frontend.md §7 gotcha 12).
 *
 * Estructura (round 2026-10-28):
 *
 *   <div .af-vp>                     contenedor propio, del ancho del lugar que lo aloja
 *     <div .af-vp-pantalla>          el «escenario»: reserva su tamaño ANTES de que llegue el video
 *       <video />                    llena el escenario (object-fit: contain)
 *     </div>
 *     <div .af-vp-barra>             fila de acciones propia, SIEMPRE dentro del contenedor:
 *       [Baja | HD]  [Descargar]  (i)
 *     </div>
 *   </div>
 *
 * Por qué el escenario: un <video> sin metadata mide 300x150 y salta a su tamaño real cuando llega
 * `loadedmetadata`; dentro de un modal eso movía todo el contenido (si el modal era centrado, lo
 * recentraba; si el video era vertical, lo estiraba por debajo de la pantalla). Ahora el
 * escenario tiene un aspect-ratio (16:9 hasta saber el real; el real se recuerda por URL) con
 * tope de alto en `dvh`, y el video lo llena: su tamaño no depende de la altura intrínseca del
 * archivo ni de la fuente (liviana o HD), así que cambiar de calidad tampoco mueve nada. Al conocer
 * `videoWidth/videoHeight` el escenario pasa al ratio real con una transición corta, sin que su
 * esquina superior se mueva.
 *
 * Calidad: si el archivo tiene versión liviana (`previewSrc`, 480p por defecto) el video se abre
 * con ella —gasta una fracción de los datos— y el selector segmentado «Baja | HD» permite pasar al
 * original y volver. Sin versión liviana (videos viejos o todavía en proceso) se reproduce el
 * original y no aparece el selector. Calidad inicial: lo que la persona eligió la última vez
 * (localStorage `af_video_calidad`); si nunca eligió, «baja» cuando el navegador pide ahorrar datos
 * y, si no, la predeterminada del sistema (`playerDefaultQuality`; «baja» mientras no carga). Al
 * cambiar de calidad en pleno video se conservan el segundo exacto, el play/pausa y la velocidad.
 * Si la versión liviana NO SE PUEDE CARGAR (el navegador no soporta la fuente / el archivo no existe,
 * `MEDIA_ERR_SRC_NOT_SUPPORTED`, el archivo está dañado, `MEDIA_ERR_DECODE`, o el error llegó antes de
 * tener la metadata), se cae UNA vez al original (sin bucle) y el selector desaparece. Un error
 * PASAJERO con la liviana ya andando (se cortó la señal del celular: `MEDIA_ERR_NETWORK`, un trozo que
 * no llegó) NO salta al HD pesado sin avisar —eso le gastaba al usuario los datos que justamente quería
 * ahorrar—: se reintenta la MISMA fuente (hasta 2 veces, con espera, en el mismo segundo) y, si no hay
 * caso, se avisa y se deja la decisión en el selector: tocar de nuevo la calidad activa recarga, y «HD»
 * pasa al original. El tope de reintentos NO se reinicia cuando vuelve la metadata (un `load()` siempre la
 * vuelve a emitir, y un corte persistente después de ella —un archivo truncado, un proxy que corta la
 * respuesta— recargaba la liviana para siempre, bajando una y otra vez el primer tramo): se reinicia solo
 * cuando el video AVANZA de verdad (`PROGRESO_QUE_REINICIA_S` segundos más allá del punto del corte) o
 * cuando la persona reintenta a mano.
 *
 * Descargar: baja el ORIGINAL (siempre HD) con `?d=1` por un ancla temporal y la sesión por cookie,
 * tras comprobar con un `HEAD` que el enlace responde (si venció o el archivo ya no existe, avisa y no
 * navega; ver utils/descargaArchivo.ts). Solo aparece si `descargable` lo permite; por defecto, solo para admin/superadmin. Cada pantalla
 * que ya deja descargar a otros roles pasa `descargable` explícito; el cliente en solo lectura
 * nunca lo ve (y con `descargable=false` se quita además la opción «Descargar» del menú nativo).
 *
 * Pantalla completa: es la nativa del <video> (solo el video y sus controles nativos); la barra
 * propia no viaja a pantalla completa. Hace falta salir de ella para cambiar de calidad o descargar.
 */
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import type {
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  SyntheticEvent,
  VideoHTMLAttributes,
} from "react";
import { message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { AuthContext } from "../context/AuthContext";
import { debeAvisarDatosMoviles } from "../utils/conexion";
import { isAdminRole } from "../utils/roles.utils";
import { descargarOriginal, pesoLegible } from "../utils/videoDescarga";
import {
  EVENTO_CALIDAD_VIDEO,
  calidadInicial,
  getVideoPlayerConfig,
  guardarCalidad,
  leerCalidadGuardada,
  leerVideoPlayerConfigEnCache,
  pideAhorroDeDatos,
  type CalidadVideo,
} from "../utils/videoPlayerConfig";
import InfoAyuda, { AyudaBloque } from "./InfoAyuda";
import "./VideoPlayer.css";

export interface VideoPlayerProps
  extends Omit<VideoHTMLAttributes<HTMLVideoElement>, "src" | "controls"> {
  /** URL del video original (HD). */
  src: string;
  /** URL firmada de la versión liviana (`entry.previewUrl`); ausente = solo hay original. */
  previewSrc?: string | null;
  /** Controles nativos (default `true`). */
  controls?: boolean;
  /** `data-testid` del <video>; la barra usa `<testId>-calidad`, `-baja`, `-hd`, `-descargar` e `-info`. */
  testId?: string;
  /** Clase del contenedor (el `className` va al <video>). */
  wrapperClassName?: string;
  /** Estilo del contenedor (el `style` va al <video>). */
  wrapperStyle?: CSSProperties;
  /**
   * ¿Se ofrece «Descargar»? Por defecto solo si la persona es admin/superadmin. Cada pantalla puede
   * forzarlo (`true` donde ese rol ya puede descargar archivos; `false` donde es solo lectura).
   */
  descargable?: boolean;
  /** Nombre con el que se ofrece el archivo al descargarlo (el navegador lo respeta si es del mismo origen). */
  nombreArchivo?: string;
  /** Peso del original en bytes (`entry.size`): se muestra junto a «HD» y en el aviso de descarga. */
  sizeBytes?: number | null;
  /** Peso de la versión liviana en bytes (`entry.previewSize`): se muestra junto a «Baja». */
  previewSizeBytes?: number | null;
  /** Relación ancho/alto del escenario mientras no se conoce la real (default 16/9). */
  ratio?: number;
  /**
   * `"ratio"` (default): el escenario toma su alto del ancho disponible y del ratio, con tope de alto.
   * `"llenar"`: el escenario llena el alto del contenedor (carruseles con slide de alto fijo).
   */
  ajuste?: "ratio" | "llenar";
  /** Tope de alto del escenario como longitud CSS (default `calc(100dvh - 240px)`, mínimo 180 px). */
  altoMaximo?: string;
}

/** Lo que hay que restaurar tras cambiar el `src` (el navegador reinicia todo al recargar). */
interface Pendiente {
  t: number;
  play: boolean;
  rate: number;
}

const RATIO_DEFECTO = 16 / 9;

/** `MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED`: no hay forma de reproducir esa fuente (formato, 404, 403). */
const ERR_FUENTE_NO_SOPORTADA = 4;
/** `MediaError.MEDIA_ERR_DECODE`: el archivo llegó pero está dañado; recargarlo no lo arregla. */
const ERR_DECODIFICACION = 3;
/** Segundos de reproducción real, más allá del punto del corte, que dan por buena la fuente y reinician el tope. */
const PROGRESO_QUE_REINICIA_S = 5;
/** Cuántas veces se reintenta la MISMA versión liviana ante un error pasajero antes de avisar. */
const REINTENTOS_RED = 2;
/** Espera base entre reintentos (se multiplica por el número de intento). */
const ESPERA_REINTENTO_MS = 1500;

/** Ratio real de cada video ya visto (por URL): la próxima apertura nace con el tamaño correcto. */
const RATIOS_VISTOS = new Map<string, number>();
const clave = (url: string) => url.split("?")[0];

/**
 * Los toques dentro de la explicación (un modal en un portal) suben por el árbol de React hasta
 * los ancestros del video; un carrusel que desliza los leería como un gesto. Los clics y las
 * teclas ya los corta InfoAyuda; los toques se cortan acá.
 */
const detener = (e: SyntheticEvent) => e.stopPropagation();

/** Explicación de la calidad y de la descarga del video, para cualquier usuario. */
function AyudaCalidad({ testId, descargable }: { testId: string; descargable: boolean }) {
  return (
    <span className="af-vp-info" onTouchStart={detener} onTouchMove={detener} onTouchEnd={detener}>
      <InfoAyuda titulo="Calidad del video" testId={testId} className="af-vp-info-btn">
        <AyudaBloque titulo="Calidad baja">
          Es una versión más liviana del mismo video. Se ve con menos detalle, pero gasta bastante
          menos datos: es lo ideal si estás con datos móviles.
        </AyudaBloque>
        <AyudaBloque titulo="Cómo pasar a HD">
          Bajo el video está el selector «Baja | HD»: toca la calidad que quieras ver y el video
          sigue en el mismo segundo. HD es el video original y gasta más datos.
        </AyudaBloque>
        <AyudaBloque titulo="Tu elección">
          Se recuerda en este dispositivo y se usa en los próximos videos. Mientras no elijas, el
          video se abre en la calidad que tenga definida el sistema (o en baja, si tu navegador
          pide ahorrar datos).
        </AyudaBloque>
        {descargable ? (
          <AyudaBloque titulo="Descargar">
            El botón «Descargar» baja siempre el original, en su calidad completa, aunque estés
            viendo la versión baja. En pantalla completa la barra no se ve: sal de ella para cambiar
            de calidad o descargar.
          </AyudaBloque>
        ) : (
          <AyudaBloque titulo="Pantalla completa">
            En pantalla completa la barra de calidad no se ve: sal de ella para cambiar entre baja y
            HD.
          </AyudaBloque>
        )}
      </InfoAyuda>
    </span>
  );
}

export default function VideoPlayer({
  src,
  previewSrc,
  controls = true,
  testId,
  wrapperClassName,
  wrapperStyle,
  descargable,
  nombreArchivo,
  sizeBytes,
  previewSizeBytes,
  ratio,
  ajuste = "ratio",
  altoMaximo,
  autoPlay,
  className,
  style,
  onError,
  onLoadedMetadata,
  onPlay,
  onTimeUpdate,
  ...resto
}: VideoPlayerProps) {
  const tienePreview = typeof previewSrc === "string" && previewSrc.length > 0;
  const videoRef = useRef<HTMLVideoElement>(null);

  // El contexto de sesión es opcional: el reproductor también se usa fuera del árbol de AuthProvider (pruebas).
  const auth = useContext(AuthContext);
  const puedeDescargar = descargable ?? isAdminRole(auth?.user?.rol);

  const [calidad, setCalidad] = useState<CalidadVideo>(() =>
    calidadInicial(leerVideoPlayerConfigEnCache()),
  );
  /** URL liviana que falló: mientras `previewSrc` siga siendo esa, se usa el original. */
  const [rotoDe, setRotoDe] = useState<string | null>(null);
  /** Tras tocar la calidad con el video ya andando, el autoplay no debe volver a dispararse. */
  const [sinAutoPlay, setSinAutoPlay] = useState(false);
  /** Relación ancho/alto del escenario: la dada, la ya vista de este video o 16:9 hasta saber la real. */
  const [relacion, setRelacion] = useState<number>(
    () => RATIOS_VISTOS.get(clave(src)) ?? (ratio && ratio > 0 ? ratio : RATIO_DEFECTO),
  );

  /** La persona tocó el selector en ESTE reproductor: nada la pisa (config ni otros players). */
  const eligioRef = useRef(false);
  /** El video ya empezó a reproducirse alguna vez. */
  const inicioRef = useRef(false);
  const pendienteRef = useRef<Pendiente | null>(null);
  /** Espejo de `calidad` para comparar dentro de callbacks asíncronos sin re-suscribir efectos. */
  const calidadRef = useRef<CalidadVideo>(calidad);
  /** Hay una descarga comprobándose: el botón queda deshabilitado (un doble toque no baja dos veces). */
  const [descargando, setDescargando] = useState(false);
  const montadoRef = useRef(true);
  /** Fuente para la que ya llegó `loadedmetadata` (distingue «nunca cargó» de «se cortó andando»). */
  const metadataDeRef = useRef<string | null>(null);
  /** Reintentos de la misma fuente tras un error pasajero, y el segundo en que se cortó la última vez. */
  const reintentosRef = useRef<{ fuente: string; n: number; desde: number }>({
    fuente: "",
    n: 0,
    desde: 0,
  });
  const temporizadorRef = useRef<number | null>(null);
  /** Fuente cuyos reintentos se agotaron: tocar la calidad activa la vuelve a cargar. */
  const sinRedDeRef = useRef<string | null>(null);

  const liviana = tienePreview && rotoDe !== previewSrc;
  const usaLiviana = liviana && calidad === "baja";
  const fuente = usaLiviana ? (previewSrc as string) : src;

  useEffect(() => {
    calidadRef.current = calidad;
  }, [calidad]);

  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
    };
  }, []);

  // Un reintento pendiente es de UNA fuente: si cambia (calidad, otro video) o se desmonta, no corre.
  useEffect(
    () => () => {
      if (temporizadorRef.current != null) {
        window.clearTimeout(temporizadorRef.current);
        temporizadorRef.current = null;
      }
    },
    [fuente],
  );

  // La configuración del sistema solo importa si hay versión liviana y la persona no tiene
  // una preferencia propia. Si ya estaba en caché, el estado inicial la usó.
  useEffect(() => {
    if (!tienePreview) return;
    if (leerCalidadGuardada() || pideAhorroDeDatos()) return;
    if (leerVideoPlayerConfigEnCache()) return;
    let vivo = true;
    void getVideoPlayerConfig().then((cfg) => {
      if (!vivo || eligioRef.current || inicioRef.current) return;
      // Mientras la configuración viajaba, la persona pudo elegir en otro reproductor: gana ella.
      if (leerCalidadGuardada()) return;
      if (calidadRef.current !== cfg.playerDefaultQuality) setCalidad(cfg.playerDefaultQuality);
    });
    return () => {
      vivo = false;
    };
  }, [tienePreview]);

  // Si la persona cambia la calidad en otro reproductor (p. ej. otra diapositiva del carrusel),
  // los que todavía no empezaron la adoptan. Los que ya andan no se tocan.
  useEffect(() => {
    if (!tienePreview) return;
    const alCambiar = (e: Event) => {
      const nueva = (e as CustomEvent<unknown>).detail;
      if (nueva !== "baja" && nueva !== "alta") return;
      if (eligioRef.current || inicioRef.current) return;
      setCalidad(nueva);
    };
    window.addEventListener(EVENTO_CALIDAD_VIDEO, alCambiar);
    return () => window.removeEventListener(EVENTO_CALIDAD_VIDEO, alCambiar);
  }, [tienePreview]);

  /** Anota segundo, play/pausa y velocidad ANTES de que cambie el `src`. */
  const capturar = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const yaAndaba = inicioRef.current || v.currentTime > 0;
    // Si hay una captura sin aplicar (cambios seguidos antes de cargar), esa manda: el
    // elemento recién recargado marcaría 0 s.
    if (!pendienteRef.current) {
      pendienteRef.current = {
        t: Number.isFinite(v.currentTime) ? v.currentTime : 0,
        play: !v.paused && !v.ended,
        rate: v.playbackRate || 1,
      };
    }
    if (yaAndaba) setSinAutoPlay(true);
  }, []);

  // Con `preload="none"` (el visor de las Notas del aseo) el navegador NO carga el archivo nuevo hasta que
  // alguien llama a play(): `loadedmetadata` nunca llega, así que el video que venía reproduciéndose quedaba
  // detenido en 0:00 tras cambiar de calidad. Se le da play de inmediato (el toque del botón es el gesto); al
  // llegar la metadata se restaura el segundo y la velocidad como siempre. Con otro `preload` el navegador
  // carga solo y no hace falta.
  useEffect(() => {
    const p = pendienteRef.current;
    const v = videoRef.current;
    if (!p?.play || !v || v.preload !== "none") return;
    try {
      const r = v.play() as Promise<void> | undefined;
      if (r && typeof r.catch === "function") r.catch(() => undefined);
    } catch {
      /* el navegador pidió un gesto: la persona le da play */
    }
  }, [fuente]);

  /** Vuelve a cargar la fuente actual (tras un error pasajero), conservando lo anotado en `pendienteRef`. */
  const recargar = useCallback(() => {
    const v = videoRef.current;
    if (!v || typeof v.load !== "function") return;
    try {
      v.load();
    } catch {
      /* el navegador no dejó recargar: queda el aviso */
    }
    // Con `preload="none"` el navegador no carga hasta que alguien llama a play(): igual que al cambiar de calidad.
    const p = pendienteRef.current;
    if (p?.play && v.preload === "none" && typeof v.play === "function") {
      try {
        const r = v.play() as Promise<void> | undefined;
        if (r && typeof r.catch === "function") r.catch(() => undefined);
      } catch {
        /* el navegador pidió un gesto: la persona le da play */
      }
    }
  }, []);

  const elegir = (nueva: CalidadVideo) => (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (nueva === calidad) {
      // Tocar de nuevo la calidad activa cuando se agotaron los reintentos de red = «volver a intentar».
      if (sinRedDeRef.current === fuente) {
        sinRedDeRef.current = null;
        reintentosRef.current = { fuente, n: 0, desde: 0 };
        capturar();
        recargar();
      }
      return;
    }
    eligioRef.current = true;
    capturar();
    setCalidad(nueva);
    guardarCalidad(nueva);
  };

  const alDescargar = async (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (descargando) return;
    setDescargando(true);
    let arranco = false;
    try {
      // Primero se comprueba el enlace (HEAD, sin cuerpo): con un token vencido o un archivo borrado
      // NO se navega —eso reemplazaba la pestaña por el JSON del error—; el motivo ya queda avisado.
      arranco = await descargarOriginal(src, nombreArchivo);
    } finally {
      if (montadoRef.current) setDescargando(false);
    }
    if (!arranco) return;
    const peso = pesoLegible(sizeBytes);
    const aviso = peso
      ? `Se descarga el video original (${peso}).`
      : "Se descarga el video original.";
    if (typeof sizeBytes === "number" && debeAvisarDatosMoviles(sizeBytes)) {
      message.warning(`${aviso} Estás con datos móviles: puede gastar buena parte de tu plan.`);
    } else {
      message.info(`Descargando… ${aviso}`);
    }
  };

  const alCargarMetadata = (e: SyntheticEvent<HTMLVideoElement>) => {
    const p = pendienteRef.current;
    const v = e.currentTarget;
    // Esta fuente sí carga: un error posterior es pasajero (se corta la señal), no «no se puede reproducir».
    // OJO: acá NO se reinicia el tope de reintentos. Cada `load()` de un reintento vuelve a emitir esta
    // metadata, así que reiniciarlo acá hacía el tope inalcanzable para un corte persistente después de
    // ella. Se reinicia en `alAvanzar` (el video avanzó de verdad) o cuando la persona reintenta.
    metadataDeRef.current = fuente;
    sinRedDeRef.current = null;
    if (v.videoWidth > 0 && v.videoHeight > 0) {
      const real = v.videoWidth / v.videoHeight;
      RATIOS_VISTOS.set(clave(src), real);
      // Las dos versiones (480p y 720p) tienen el mismo ratio salvo por redondeos: no se toca por menos de 2 %.
      setRelacion((actual) => (Math.abs(actual - real) / actual > 0.02 ? real : actual));
    }
    if (p) {
      pendienteRef.current = null;
      try {
        v.playbackRate = p.rate;
        if (p.t > 0) {
          const tope = Number.isFinite(v.duration) && v.duration > 0 ? v.duration : p.t;
          v.currentTime = Math.min(p.t, tope);
        }
      } catch {
        /* algunos navegadores no dejan fijar el tiempo antes de tiempo: se parte del inicio */
      }
      if (p.play && typeof v.play === "function") {
        try {
          const r = v.play() as Promise<void> | undefined;
          if (r && typeof r.catch === "function") r.catch(() => undefined);
        } catch {
          /* el navegador pidió un gesto: la persona le da play */
        }
      }
    }
    onLoadedMetadata?.(e);
  };

  const alReproducir = (e: SyntheticEvent<HTMLVideoElement>) => {
    inicioRef.current = true;
    onPlay?.(e);
  };

  /** El video AVANZÓ de verdad más allá del último corte: la fuente anda, los reintentos vuelven a estar completos. */
  const alAvanzar = (e: SyntheticEvent<HTMLVideoElement>) => {
    const r = reintentosRef.current;
    if (r.n > 0 && r.fuente === fuente) {
      const t = e.currentTarget.currentTime;
      if (Number.isFinite(t) && t - r.desde >= PROGRESO_QUE_REINICIA_S) {
        reintentosRef.current = { fuente, n: 0, desde: 0 };
      }
    }
    onTimeUpdate?.(e);
  };

  const alFallar = (e: SyntheticEvent<HTMLVideoElement>) => {
    if (usaLiviana) {
      const codigo = e.currentTarget.error?.code;
      if (
        codigo === ERR_FUENTE_NO_SOPORTADA ||
        codigo === ERR_DECODIFICACION ||
        metadataDeRef.current !== fuente
      ) {
        // La versión liviana NO SE PUEDE cargar (formato, archivo ausente o dañado) o ni siquiera llegó a
        // tener metadata: se cae al original (una sola vez: `rotoDe` queda fijo para este `previewSrc`) y no
        // se avisa como error, porque el video sí va a poder verse.
        capturar();
        setRotoDe(previewSrc as string);
        return;
      }
      // Estaba andando y se cortó (señal móvil intermitente): NO se salta al HD pesado sin avisar.
      // Se reintenta la misma fuente y, si no hay caso, se avisa y manda la persona.
      const previos = reintentosRef.current.fuente === fuente ? reintentosRef.current.n : 0;
      if (previos >= REINTENTOS_RED) {
        sinRedDeRef.current = fuente;
        message.warning(
          "Se interrumpió el video por la conexión. Toca de nuevo la calidad que estás viendo para reintentar, o elige HD.",
        );
        return;
      }
      capturar();
      // El segundo del corte es el que quedó anotado para retomar (`pendienteRef`): si el reintento falla
      // otra vez ANTES de tener metadata, el elemento recargado marca 0 s y no sirve de referencia.
      const corte = pendienteRef.current?.t ?? e.currentTarget.currentTime;
      reintentosRef.current = { fuente, n: previos + 1, desde: Number.isFinite(corte) ? corte : 0 };
      if (temporizadorRef.current != null) window.clearTimeout(temporizadorRef.current);
      temporizadorRef.current = window.setTimeout(() => {
        temporizadorRef.current = null;
        recargar();
      }, ESPERA_REINTENTO_MS * (previos + 1));
      return;
    }
    pendienteRef.current = null;
    onError?.(e);
  };

  const base = testId ?? "video";
  const pesoBaja = pesoLegible(previewSizeBytes);
  const pesoHd = pesoLegible(sizeBytes);
  const hayBarra = liviana || puedeDescargar;

  const estiloPantalla = {
    "--af-vp-ratio": String(relacion),
    ...(altoMaximo ? { "--af-vp-alto-max": altoMaximo } : null),
  } as CSSProperties;
  const clases = ["af-vp", ajuste === "llenar" ? "af-vp--llenar" : "", wrapperClassName ?? ""]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={clases} style={wrapperStyle} data-testid={testId ? `${testId}-contenedor` : undefined}>
      <div className="af-vp-pantalla" style={estiloPantalla}>
        <video
          {...resto}
          ref={videoRef}
          className={className}
          style={style}
          src={fuente}
          controls={controls}
          // Sin permiso para descargar, tampoco se ofrece la opción «Descargar» del menú nativo.
          controlsList={resto.controlsList ?? (puedeDescargar ? undefined : "nodownload")}
          autoPlay={autoPlay && !sinAutoPlay}
          data-testid={testId}
          data-calidad={liviana ? (usaLiviana ? "baja" : "alta") : undefined}
          onError={alFallar}
          onLoadedMetadata={alCargarMetadata}
          onPlay={alReproducir}
          onTimeUpdate={alAvanzar}
        />
      </div>
      {hayBarra ? (
        <div className="af-vp-barra" role="toolbar" aria-label="Opciones del video">
          {liviana ? (
            <div
              className="af-vp-calidad"
              role="group"
              aria-label="Calidad del video"
              data-testid={`${base}-calidad`}
            >
              <button
                type="button"
                className="af-vp-seg"
                data-testid={`${base}-baja`}
                aria-pressed={usaLiviana}
                aria-label="Ver en calidad baja (ahorra datos)"
                title="Ver en calidad baja (ahorra datos)"
                onClick={elegir("baja")}
              >
                <span className="af-vp-seg-nombre">Baja</span>
                {pesoBaja ? <span className="af-vp-seg-peso">{pesoBaja}</span> : null}
              </button>
              <button
                type="button"
                className="af-vp-seg"
                data-testid={`${base}-hd`}
                aria-pressed={!usaLiviana}
                aria-label="Ver en HD (gasta más datos)"
                title="Ver en HD (gasta más datos)"
                onClick={elegir("alta")}
              >
                <span className="af-vp-seg-nombre">HD</span>
                {pesoHd ? <span className="af-vp-seg-peso">{pesoHd}</span> : null}
              </button>
            </div>
          ) : null}
          <span className="af-vp-relleno" />
          {puedeDescargar ? (
            <button
              type="button"
              className="af-vp-accion"
              data-testid={`${base}-descargar`}
              aria-label={
                pesoHd
                  ? `Descargar el video original (${pesoHd})`
                  : "Descargar el video original"
              }
              title="Descargar el video original"
              disabled={descargando}
              aria-busy={descargando}
              onClick={alDescargar}
            >
              <DownloadOutlined aria-hidden="true" />
              <span className="af-vp-accion-texto">Descargar</span>
            </button>
          ) : null}
          {liviana ? (
            <AyudaCalidad testId={testId ? `${testId}-info` : "video-calidad-info"} descargable={puedeDescargar} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
